import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { DatePicker } from '@/components/ui/DatePicker'
import { stockDateEditSchema, type StockDateEditValues } from '../types/schemas'
import type { StockDateEditModalProps } from '../types'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'

function normalizeDate(value: string | undefined): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export function StockDateEditModal({
  isOpen,
  stock,
  dateType,
  submitting,
  onClose,
  onSubmit,
}: StockDateEditModalProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<StockDateEditValues>({
    resolver: zodResolver(stockDateEditSchema),
    mode: 'onChange',
    defaultValues: { effective_date: '', issued_date: '' },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      effective_date: normalizeDate(stock?.effective_date),
      issued_date: normalizeDate(stock?.issued_date),
    })
  }, [isOpen, stock, reset])

  const internalSubmit = (values: StockDateEditValues) => {
    if (!stock) return
    onSubmit(stock.stock_id, {
      effective_date: values.effective_date || normalizeDate(stock.effective_date),
      issued_date: values.issued_date || normalizeDate(stock.issued_date),
    })
  }

  const title = dateType === 'stock_effective_date' ? 'Stock Effective Date' : 'Stock Issued Date'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-md">
      <form onSubmit={handleSubmit(internalSubmit)} className="space-y-4 p-2">
        {dateType === 'stock_effective_date' ? (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="stock-edit-effective">
              Effective Date
            </label>
            <Controller
              name="effective_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="stock-edit-effective"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  allowFutureDates
                  showHelperText={false}
                  popperFixed
                  popperPlacement="top-start"
                  placeholder="MM/DD/YYYY"
                  error={errors.effective_date?.message}
                />
              )}
            />
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="stock-edit-issued">
              Issued Date
            </label>
            <Controller
              name="issued_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="stock-edit-issued"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  allowFutureDates
                  showHelperText={false}
                  popperFixed
                  popperPlacement="top-start"
                  placeholder="MM/DD/YYYY"
                  error={errors.issued_date?.message}
                />
              )}
            />
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isDirty || submitting}
            className={primaryEntityActionButtonClass}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={secondaryEntityActionButtonClass}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
